import React from 'react';
import styled from 'styled-components';
import { getPaymentLogoImage } from '@/util/payment';
import uploadSuccessImage from '@/assets/refund/check_circle.png';

type Props = {
  cancelDate: string;
  totalPrice: string;
  onClickBtn: () => void;
  btnTitle: string;
  btnDisabled?: boolean;
  paymentDate?: string;
  nationality?: string;
  passportNumber?: string;
  totalAmount?: number;
  refundAmount?: number;
};

function RefundCancelCompleteModal({
  cancelDate,
  totalPrice,
  onClickBtn,
  btnTitle,
  btnDisabled = false,
  paymentDate,
  nationality,
  passportNumber,
  totalAmount,
  refundAmount,
}: Props) {
  return (
    <Wrapper>
      <Container>
        <CheckIcon src={uploadSuccessImage} alt='체크 아이콘' />
        <Desc>환급 취소되었습니다</Desc>

        <InfoSection>
          <SectionTitle>취소 정보</SectionTitle>
          <InfoInner>
            <InfoRow>
              <InfoLabel>취소 일시</InfoLabel>
              <InfoValueSmall>{cancelDate}</InfoValueSmall>
            </InfoRow>
            <HorizontalLine />
          </InfoInner>
        </InfoSection>

        <InfoSection>
          <SectionTitle>환급 정보</SectionTitle>
          <InfoInner>
            <InfoRow>
              <InfoLabel>시술 일시</InfoLabel>
              <InfoValueSmall>{paymentDate}</InfoValueSmall>
            </InfoRow>
            <AdditionalInfo>
              <HorizontalLine />
              <InfoRow>
                <InfoLabel>국적</InfoLabel>
                <InfoValue>{nationality}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>여권번호</InfoLabel>
                <InfoValue>{passportNumber}</InfoValue>
              </InfoRow>
              <HorizontalLine />
              <InfoRow>
                <InfoLabel>의료 금액</InfoLabel>
                <InfoValue>{totalAmount?.toLocaleString()}원</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>환급 금액</InfoLabel>
                <InfoValue>{refundAmount?.toLocaleString()}원</InfoValue>
              </InfoRow>
            </AdditionalInfo>

            <HorizontalLine />
            <InfoRow>
              <InfoLabel>결제 금액</InfoLabel>
              <InfoLabelLarge>{totalPrice}원</InfoLabelLarge>
            </InfoRow>
          </InfoInner>
        </InfoSection>

        <ButtonWrapper>
          <ConfirmButton
            color='#FFFFFF'
            bgColor='#246CF6'
            onClick={onClickBtn}
            disabled={btnDisabled}
          >
            {btnTitle}
          </ConfirmButton>
        </ButtonWrapper>
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  z-index: 600;
  background-color: rgba(33, 33, 33, 0.1);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 672px;
  padding: 56px 32px 32px;
  position: relative;
  border-radius: 8px;
  background-color: ${(props) => props.theme.pointColors.white};
  filter: ${(props) => props.theme.boxShadow};
`;

const CheckIcon = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
`;

const Desc = styled.p`
  margin: 0 0 24px 0;

  color: var(--Mono-80, #3a3b3e);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 36px */
  letter-spacing: -0.48px;
`;

const InfoSection = styled.div`
  width: 100%;
  margin-bottom: 32px;
`;

const AdditionalInfo = styled.div``;

const SectionTitle = styled.h3`
  display: flex;
  padding: 0 20px 15px;
  border-bottom: 1px solid #e5e6e8;
  color: var(--KTP-Mono-100, var(--KTP-Color-Mono-100, #030303));

  /* KTPHead/02_18SB */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 27px */
`;
const InfoInner = styled.div`
  padding: 0 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 44px;
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

const HorizontalLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e6e8;
  margin: 4px 0;
`;

const InfoLabel = styled.div`
  color: var(--KTP-Mono-65, var(--KTP-Color-Mono-65, #5f6165));

  /* KTPBody/02_14MD */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const InfoLabelLarge = styled(InfoLabel)`
  letter-spacing: 0;
  font-size: 16px;
  color: var(--KTP-Mono-100, var(--KTP-Color-Mono-100, #030303));
`;

const InfoValue = styled.div`
  color: var(--KTP-Mono-65, var(--KTP-Color-Mono-65, #5f6165));
  text-align: right;

  /* KTPBody/02_14MD */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
`;

const InfoValueSmall = styled(InfoValue)`
  font-size: 14px;

  letter-spacing: -0.28px;
`;

const InfoValueLarge = styled(InfoValue)`
  letter-spacing: 0;
  font-size: 16px;
  color: var(--KTP-Mono-100, var(--KTP-Color-Mono-100, #030303));
`;

const PaymentValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const PaymentMethodBadge = styled.span`
  background-color: #f5f6f7;
  color: #3a3b3e;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
`;

const PaymentAmount = styled.div`
  color: #246cf6;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
`;

const Button = styled.button<{
  bgColor: string;
  color: string;
  btnDisabled?: boolean;
}>`
  flex: 1;
  border-radius: 8px;
  padding: 14px;
  font-size: ${(props) => props.theme.fontSize.small};
  color: ${(props) => props.color};
  background-color: ${(props) => (props.disabled ? '#D3E2FD' : props.bgColor)};
  line-height: 150%;
  font-weight: 600;
`;

const ConfirmButton = styled(Button)`
  flex: 1;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  width: 100%;
`;

export default RefundCancelCompleteModal;
